import React, { useEffect } from 'react';
import clsx from 'clsx';
import { useTheme } from 'next-themes';
import Image from 'components/commercetools-ui/atoms/image';
import { WHITE_COLOR_HEX } from 'helpers/constants';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mediumDesktop } from 'helpers/utils/screensizes';
import { CENTER, COVER, fontWeightMap } from './config';
import { Props } from './types';
import Link from '../../atoms/link';

const Tile: React.FC<Props> = ({ keyProp, tile, backgroundColor, backgroundImage, isCarousel = true }) => {
  const { headLine } = tile;

  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const tileImageSizes = useImageSizes({
    sm: 0.2,
    md: 0.2,
    lg: 0.15,
    defaultSize: 0.15,
    maxWidth: 264,
  });
  const { resolvedTheme = 'light' } = useTheme();
  const [themeColor, setThemeColor] = React.useState('');
  const getBackgroundColor = () => {
    if (backgroundColor) {
      return themeColor === 'dark' ? backgroundColor.darkColor : backgroundColor.lightColor;
    }
    return WHITE_COLOR_HEX; // default color
  };

  // Prepare background style using user's logic
  const backgroundStyle: React.CSSProperties = {
    backgroundColor: getBackgroundColor(),
    ...(backgroundImage && {
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: COVER,
      backgroundPosition: CENTER,
    }),
  };
  useEffect(() => {
    setThemeColor(resolvedTheme ?? '');
  }, [resolvedTheme]);
  return (
    <div
      className={isCarousel ? 'pb-8 focus:outline-offset-[-2px]' : ''}
      id="carousel-tile-wrapper"
      data-ffid={`ctr-productcategory-tile-wrapper-${keyProp}`}
    >
      <div
        className={clsx(
          'flex w-full flex-col items-center rounded-xl md:rounded-2xl lg:h-360',
          isCarousel ? 'mt-25' : '',
          isDesktopSize ? 'lift-target' : '',
        )}
        style={backgroundStyle}
      >
        <Link
          link={tile.target}
          className={clsx(
            'flex aspect-1 w-full flex-col items-center gap-20 rounded-xl pb-20 md:gap-26 md:rounded-2xl md:pb-26',
          )}
          id="carousel-tile"
          style={backgroundStyle}
          data-ffid={`lnk-productcategory-tile-${keyProp}`}
        >
          <div
            className={clsx('relative aspect-1 w-full sm:aspect-1')}
            data-ffid={`ctr-productcategory-img-container-${keyProp}`}
          >
            <Image
              {...tile.image}
              sizes={tileImageSizes}
              alt={tile?.title ?? tile.imageAlt ?? tile.image.alt}
              fill
              style={{ objectFit: COVER }}
              className="size-full rounded-xl opacity-100 brightness-100 md:rounded-2xl lg:left-3 lg:top-3 lg:rotate-0"
              data-ffid={`img-productcategory-tile-${keyProp}`}
              unoptimized={false}
            />
          </div>
          <h3
            className={clsx(
              headLine?.[0]?.textStyle ? fontWeightMap[headLine[0].textStyle] : 'font-normal',
              'max-w-full truncate text-center',
            )}
            style={{
              fontSize: isDesktopSize ? `${headLine?.[0]?.fontSizeDesktop}px` : `${headLine?.[0]?.fontSizeMobile}px`,
              color: themeColor === 'dark' ? headLine?.[0]?.textColorDark : headLine?.[0]?.textColor,
            }}
            title={tile.title}
            data-ffid={`lbl-productcategory-title-${keyProp}`}
          >
            {tile.title}
          </h3>
        </Link>
      </div>
    </div>
  );
};

export default Tile;
